export const STARTUP_TRACE_PREFIX = "hover-idle:startup:";
export const STARTUP_REWARD_MEASURE_NAME =
  "hover-idle:startup:offline-reward-runtime";

export type StartupTraceMark =
  | "navigation-start"
  | "script-boot-start"
  | "save-read-start"
  | "save-read-complete"
  | "decode-normalize-complete"
  | "save-restore-complete"
  | "offline-reward-elapsed-determined"
  | "offline-reward-calculation-start"
  | "offline-reward-calculation-complete"
  | "offline-reward-validation-complete"
  | "atomic-commit-start"
  | "atomic-commit-complete"
  | "live-model-initialization-start"
  | "live-model-initialization-complete"
  | "committed-model-reconstruction-complete"
  | "offline-result-ui-created"
  | "offline-result-first-paint"
  | "game-surface-first-paint"
  | "startup-complete"
  | "interactive";

export interface StartupTraceMetrics {
  readonly navigationToSaveRestoreMs: number | null;
  readonly saveRestoreToRewardStartMs: number | null;
  readonly saveRestoreToCommitCompleteMs: number | null;
  readonly rewardStartToCommitCompleteMs: number | null;
  readonly commitToResultFirstPaintMs: number | null;
  readonly offlineDecisionToResultFirstPaintMs: number | null;
  readonly navigationToResultFirstPaintMs: number | null;
  readonly navigationToInteractiveMs: number | null;
}

export interface StartupRewardTimingDetail {
  readonly inputPreparationMs: number;
  readonly mainThreadTasksMs: readonly number[];
  readonly mainThreadSingleTaskMs: number;
  readonly mainThreadTotalBlockingMs: number;
  readonly commitMs: number;
  readonly endToEndMs: number;
}

export function startupTraceMarkName(mark: StartupTraceMark): string {
  return `${STARTUP_TRACE_PREFIX}${mark}`;
}

export function markStartupTrace(
  mark: StartupTraceMark,
  startTime?: number,
): void {
  if (
    typeof performance === "undefined" ||
    typeof performance.mark !== "function"
  ) {
    return;
  }
  const name = startupTraceMarkName(mark);
  if (performance.getEntriesByName(name, "mark").length > 0) return;
  if (startTime === undefined) {
    performance.mark(name);
  } else {
    performance.mark(name, { startTime });
  }
}

export function readStartupTraceMetrics(
  entries: readonly Pick<
    PerformanceEntry,
    "name" | "startTime"
  >[] = typeof performance === "undefined"
    ? []
    : performance.getEntriesByType("mark"),
): StartupTraceMetrics {
  const marks = new Map(
    entries
      .filter((entry) => entry.name.startsWith(STARTUP_TRACE_PREFIX))
      .map((entry) => [entry.name, entry.startTime]),
  );
  const duration = (
    start: StartupTraceMark,
    end: StartupTraceMark,
  ): number | null => {
    const startTime = marks.get(startupTraceMarkName(start));
    const endTime = marks.get(startupTraceMarkName(end));
    return startTime === undefined || endTime === undefined
      ? null
      : Math.max(0, endTime - startTime);
  };
  return {
    navigationToSaveRestoreMs: duration(
      "navigation-start",
      "save-restore-complete",
    ),
    saveRestoreToRewardStartMs: duration(
      "save-restore-complete",
      "offline-reward-calculation-start",
    ),
    saveRestoreToCommitCompleteMs: duration(
      "save-restore-complete",
      "atomic-commit-complete",
    ),
    rewardStartToCommitCompleteMs: duration(
      "offline-reward-calculation-start",
      "atomic-commit-complete",
    ),
    commitToResultFirstPaintMs: duration(
      "atomic-commit-complete",
      "offline-result-first-paint",
    ),
    offlineDecisionToResultFirstPaintMs: duration(
      "offline-reward-elapsed-determined",
      "offline-result-first-paint",
    ),
    navigationToResultFirstPaintMs: duration(
      "navigation-start",
      "offline-result-first-paint",
    ),
    navigationToInteractiveMs: duration("navigation-start", "interactive"),
  };
}

export function measureStartupReward(detail: StartupRewardTimingDetail): void {
  if (
    typeof performance === "undefined" ||
    typeof performance.measure !== "function" ||
    performance.getEntriesByName(STARTUP_REWARD_MEASURE_NAME, "measure")
      .length > 0
  ) {
    return;
  }
  performance.measure(STARTUP_REWARD_MEASURE_NAME, {
    start: startupTraceMarkName("offline-reward-calculation-start"),
    end: startupTraceMarkName("atomic-commit-complete"),
    detail: {
      ...detail,
      mainThreadTasksMs: [...detail.mainThreadTasksMs],
    },
  });
}
